import React, { useState } from 'react';
import moment from 'moment';
import { useNotify, useTranslate } from 'react-admin';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { Checkbox, TextField } from '@material-ui/core';
import { apiPost } from '../brainApi';
import { convertPhoneFormat, getZ3PConfigValue } from '../helper';
import { sendAnalyticsEvent } from '../analytics';
import DialogTitle from '../components/DialogTitle';

const formatTime = (time) => {
  const m = moment.utc(time).local(true);

  // Round to next 15 minutes
  const remainder = m.minute() % 15;
  m.add(15 - remainder, 'minutes');

  return m.format('h:mma');
};

const getTodayOrTomorrow = (arrival) => {
  const m = moment.utc(arrival).local(true);
  const today = moment();
  const tomorrow = moment().add(1, 'day');

  if (m.isSame(today, 'day')) {
    return 'today ';
  }
  if (m.isSame(tomorrow, 'day')) {
    return 'tomorrow ';
  }
  return '';
};

const getDetailerOrTechnician = () => {
  const z3pClient = getZ3PConfigValue('z3p_client_name');
  if (z3pClient && z3pClient.toLowerCase().includes('detail')) {
    return 'detailer';
  }
  return 'technician';
};

const generateMessage = (arrival) => {
  const arrivalFormatted = formatTime(arrival);
  const dateFormatted = getTodayOrTomorrow(arrival);
  const detailerOrTechnician = getDetailerOrTechnician();

  return `Hi- Your ${detailerOrTechnician} will arrive ${dateFormatted}around ${arrivalFormatted}. Please let us know if you have any questions. Thanks!`;
};

const useStyles = makeStyles({
  messageBox: {
    width: '360px',
  },
  disabled: {
    opacity: 0.4,
  },
});

const EtaSmsModal = ({ stops, open, setOpen }) => {
  const notify = useNotify();
  const translate = useTranslate();
  const classes = useStyles();

  // Use a set to make sure we only include one row per phone number.
  const phones = new Set();

  const defaultRows = [];
  stops.forEach(({ estimated_arrival_time, scheduled_works }) => {
    scheduled_works.forEach(({ customer }) => {
      const phone = customer?.phone;
      const customer_id = customer?.customer_id;
      const name = customer
        ? `${customer.first_name} ${customer.last_name}`
        : '';
      if (!phones.has(phone)) {
        defaultRows.push({
          phone,
          customer_id,
          name,
          checked: true,
          message: generateMessage(estimated_arrival_time),
          arrival: estimated_arrival_time,
        });
        phones.add(phone);
      }
    });
  });
  const [rows, setRows] = useState(defaultRows);

  const editValue = (phone, key, value) => {
    setRows((prevRows) =>
      prevRows.map((r) => {
        if (r.phone === phone) {
          r[key] = value;
        }
        return r;
      }),
    );
  };

  const onSubmit = (values) => {
    console.log(values);

    const payload = [];
    values.forEach((row) => {
      if (row.checked) {
        payload.push({
          customer_id: row.customer_id,
          message: row.message,
          title: 'Route ETA Message',
        });
      }
    });
    console.log(payload);

    apiPost('/sms/bulk', { messages: payload });
    // Don't wait for the response because it currently takes up to 60 seconds.
    notify(`Sent ${payload.length} message${payload.length > 1 ? 's' : ''}.`);
    sendAnalyticsEvent('messageSent', { template: 'RouteETABulk' });
    setOpen(false);
  };

  const countCheckedRows = (rows) => {
    return rows.filter((r) => r.checked).length;
  };

  return (
    <Dialog
      onClose={() => setOpen(false)}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <DialogTitle onClose={() => setOpen(false)} id="form-dialog-title">
        Send ETAs via SMS Message
      </DialogTitle>
      <DialogContent>
        <Table aria-label="simple table" size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="none" />
              <TableCell padding="none">Customer</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.phone}>
                <TableCell padding="none">
                  <Checkbox
                    onClick={() =>
                      editValue(row.phone, 'checked', !row.checked)
                    }
                    checked={row.checked}
                    color="primary"
                  />
                </TableCell>
                <TableCell
                  padding="none"
                  className={row.checked ? '' : classes.disabled}
                >
                  {row.name}
                  <br />
                  {convertPhoneFormat(row.phone)}
                </TableCell>
                <TableCell className={row.checked ? '' : classes.disabled}>
                  <TextField
                    multiline
                    className={classes.messageBox}
                    defaultValue={row.message}
                    onChange={(e) =>
                      editValue(row.phone, 'message', e.target.value)
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>
          {translate('resources.dispatch.actions.cancel')}
        </Button>
        <Button
          onClick={() => onSubmit(rows)}
          color="primary"
          variant="contained"
          disabled={countCheckedRows(rows) === 0}
        >
          {translate('resources.dispatch.actions.send', {
            smart_count: countCheckedRows(rows),
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EtaSmsModal;
